import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { MainNav } from './MainNav';
import { SubNav } from './SubNav';
import { mainPadding } from './mainPadding';
import { BottomBar } from './BottomBar';
import { installCopyFocusValue } from '@/shared/copyFocusValue';
import { DialogHost } from '@/components/ui/DialogHost';
import { ToastHost } from '@/components/ui/Toast';
import { CopilotWidget } from '@/features/copilot/CopilotWidget';
import { CommandPalette } from './CommandPalette';
import { SkipLink } from './SkipLink';
import { RouteFocus } from './RouteFocus';
import { HistoryBridge, useMouseBackButton } from './HistoryBridge';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { useZoomFocusRestore, useZoomNavWatcher } from './zoomNavigation';
import { PageHeadingProvider, RouteHeading } from './PageHeading';
import { HelpCenter } from '@/features/help/HelpCenter';
import { RecentTracker } from '@/features/recents/RecentTracker';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useTableArrowNav } from '@/shared/hooks/useTableArrowNav';
import { useAuthStore } from '@/shared/store/authStore';
import { IdleLogoutWatcher } from '@/shared/hooks/useIdleLogout';
import { useNavPrefs } from '@/shared/store/navPrefsStore';
import { useCopilotLayout } from '@/shared/store/copilotLayoutStore';
import { usePermissionsStore } from '@/shared/store/permissionsStore';
import { useBranchesStore } from '@/shared/store/branchesStore';
import { useFavoritesStore } from '@/features/favorites/useFavoritesStore';
import { useRecentsStore } from '@/features/recents/useRecentsStore';
import { usePageSizeStore } from '@/shared/store/pageSizeStore';
import { useBottomLayout } from '@/shared/store/bottomChromeStore';
import { isPlatformAccount } from '@/shared/platformAccount';
import { DemoPrintWatermark } from './DemoPrintWatermark';

// Page-level Suspense fallback that only surfaces a spinner if the lazy
// chunk takes longer than ~180ms — cached chunks resolve instantly so the
// user sees nothing. Mirrors App.tsx's SoftFallback but lives here so the
// chrome (MainNav, SubNav, DialogHost) stays mounted while a page suspends
// — without this, every menu click unmounts the whole layout for one frame
// and the page canvas flashes through, reading as a "black border / flicker"
// around the chrome.
function PageBoundaryFallback() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 180);
    return () => clearTimeout(t);
  }, []);
  if (!visible) return null;
  return <div className="p-10 flex justify-center"><LoadingSpinner /></div>;
}

export function AppLayout() {
  // One global handler gives every <tbody> in the app Excel-style arrow-key
  // navigation between cells. Per-page tables don't need to opt in.
  useTableArrowNav();
  const { reserve } = useBottomLayout();
  // Only the one page and its bar for the platform login: no company tools.
  const platform = useAuthStore((s) => isPlatformAccount(s.user));

  // Ctrl/Cmd+C on a focused field (no selection) copies its value with a
  // "Copied" bubble — works everywhere, including read-only value boxes.
  useEffect(() => installCopyFocusValue(), []);

  // Hydrate the per-user nav prefs from the server once when a user is
  // signed in. Re-runs only when the user id flips (login / re-auth) —
  // switching company keeps the same user so stays a no-op. The
  // localStorage cache already painted the navbar in their saved order;
  // this round-trip just reconciles with the canonical server copy.
  const userId = useAuthStore((s) => s.user?.id);
  // Re-hydrate when (a) the user changes (login / re-auth) OR (b) the
  // active company changes — RBAC + branches are scoped per-company.
  const companyKey = useAuthStore((s) => s.user?.company_id);
  useEffect(() => {
    if (userId) {
      void useNavPrefs.getState().hydrate();
      void useCopilotLayout.getState().hydrate();
      void usePermissionsStore.getState().hydrate();
      void useFavoritesStore.getState().hydrate();
      void useRecentsStore.getState().hydrate();
      void usePageSizeStore.getState().hydrate();
      // Branches: pass user id (for localStorage keying) and the
      // server-side default so first-login picks something sensible.
      const u = useAuthStore.getState().user as { id: string; default_branch_id?: string | null } | null;
      void useBranchesStore.getState().hydrate(u?.id, u?.default_branch_id ?? null);
    }
  }, [userId, companyKey]);

  // The zoom path on every navigation (zoomNavigation.ts).
  useZoomNavWatcher();
  const location = useLocation();

  // Zoom-out focus restore (zoomNavigation.ts).
  useZoomFocusRestore();

  // The mouse back side button on a zoomed record (D1, HistoryBridge.tsx).
  useMouseBackButton();

  // Publish the real nav height (2 rows, or 3 when a section has a third-level
  // nav) as --app-nav-h so fixed chrome — the docked AI Companion — sits below
  // it regardless of nav depth instead of assuming a fixed 80px.
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const apply = () => document.documentElement.style.setProperty('--app-nav-h', `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <PageHeadingProvider>
    <div className="min-h-screen bg-canvas">
      <SkipLink />
      <DemoPrintWatermark />
      {/* Sticky wrapper for the whole nav chrome. Each of MainNav/SubNav is
          itself `sticky`, but a sticky child can never leave its parent's box
          — so an ordinary wrapper (only ~72px tall) let them scroll away once
          the page scrolled past the nav. Making the wrapper sticky freezes the
          entire nav stack (first/second/third-level tabs) at the top always.
          z-50 keeps it above scrolling page content, the docked copilot (z-30)
          and BottomBar (z-20), and below the CommandPalette (z-100). */}
      <div ref={navRef} className="sticky top-0 z-50">
        <MainNav />
        {!platform && <SubNav />}
      </div>
      {!platform && <RecentTracker />}
      <RouteFocus />
      <HistoryBridge />
      {/* Idle timeout: warn at T-2min, sign out at 30min of inactivity
          (server enforces the same window on sessions.last_used_at). */}
      <IdleLogoutWatcher active={!!userId} />
      {/* marginRight reserves space for the docked copilot panel (0 when
          floating) so the content shrinks beside it; the top nav stays
          full-width above the panel. */}
      {/* pb matches the bottom-bar mask band (48px) + 4px, no more — extra
          padding would force a scrollbar even when the content fits the
          viewport (e.g. a Home layout sized to the screen must not scroll). */}
      <main id="main" tabIndex={-1} className={`${mainPadding(location.pathname)} pt-0 ${reserve ? 'pb-[52px]' : 'pb-4'}`} style={{ marginRight: 'var(--copilot-dock, 0px)' }}>
        <RouteHeading />
        {/* A page that throws keeps the shell: its own "failed to load" note. */}
        <RouteErrorBoundary>
          <Suspense fallback={<PageBoundaryFallback />}>
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
      </main>
      {/* App-wide imperative dialogs — anything that calls dialog.confirm /
          dialog.error / etc. surfaces here instead of the native prompts. */}
      <DialogHost />
      <BottomBar />
      <ToastHost />
      {/* Global AI copilot — floating launcher + slide-over, available on
          every page (⌘/Ctrl-K), instead of taking a nav slot. */}
      {!platform && <CopilotWidget />}
      {/* Global command palette — jump to any page / quick "New …" actions
          (⌘/Ctrl+Shift+P, or the top-nav search button). */}
      {!platform && <CommandPalette />}
      {/* Global Help Center slide-over — opened from the top-nav "?", the AI
          Companion "?", or Settings → Help & Support. */}
      {!platform && <HelpCenter />}
    </div>
    </PageHeadingProvider>
  );
}
